import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { sanityFetch } from "../sanityClient";
import ROUTES from "../routes";

type MarketStats = {
  annualVisitors: { value: string; label: string };
  vendorCount: { value: string; label: string };
  marketsPerYear: { value: string; label: string };
  googleRating: { value: string; label: string };
};

const FALLBACK_STATS = [
  { value: "225K+", label: "Annual Visitors" },
  { value: "3,124", label: "Vendors in 2025" },
  { value: "52", label: "Markets per Year" },
  { value: "5.0★", label: "Google Rating" },
];

export default function Welcome() {
  const [stats, setStats] = useState(FALLBACK_STATS);

  useEffect(() => {
    sanityFetch<MarketStats>(
      `*[_type == "marketStats"][0]{ annualVisitors, vendorCount, marketsPerYear, googleRating }`
    ).then((data) => {
      if (!data) return;
      setStats([
        data.annualVisitors,
        data.vendorCount,
        data.marketsPerYear,
        data.googleRating,
      ]);
    });
  }, []);
  return (
    <section
      style={{
        background:
          "linear-gradient(160deg, #f2fbee 0%, #e8f8e0 40%, #f4fcf0 70%, #fffdf8 100%)",
        padding: "clamp(64px, 10vw, 110px) 0 clamp(48px, 8vw, 80px)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative rings */}
      <div className="wl-ring wl-ring-1" />
      <div className="wl-ring wl-ring-2" />
      <div className="wl-blob wl-blob-1" />
      <div className="wl-blob wl-blob-2" />

      <div
        style={{
          maxWidth: "980px",
          margin: "0 auto",
          padding: "0 clamp(16px, 4vw, 32px)",
          position: "relative",
          zIndex: 1,
          textAlign: "center",
        }}
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(109,199,68,0.12)",
              border: "1px solid rgba(109,199,68,0.3)",
              borderRadius: "999px",
              padding: "9px 22px",
              color: "#2d6a14",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "2px",
              textTransform: "uppercase",
              marginBottom: "22px",
            }}
          >
            🧺 Our Community
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          style={{
            fontSize: "clamp(2.2rem, 7vw, 4.5rem)",
            fontWeight: 900,
            fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
            letterSpacing: "-2px",
            lineHeight: 1.05,
            margin: "0 0 clamp(16px, 3vw, 26px)",
            color: "#0d1b2a",
          }}
        >
          Welcome to{" "}
          <span
            style={{
              background: "linear-gradient(90deg, #2d6a14, #6dc744)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}
          >
            St. John's
          </span>
          <br />
          Farmers' Market
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.2 }}
          style={{
            color: "#4a5568",
            fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
            maxWidth: "640px",
            margin: "0 auto clamp(36px, 6vw, 56px)",
            lineHeight: 1.75,
          }}
        >
          Home to a diverse community of local vendors — passionate makers,
          bakers, and growers bringing{" "}
          <strong style={{ color: "#2d6a14" }}>fresh, handcrafted,</strong> and
          locally sourced products to Newfoundland every week.
        </motion.p>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.3 }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "clamp(12px, 2.5vw, 20px)",
            marginBottom: "clamp(40px, 6vw, 60px)",
          }}
        >
          {stats.map((s, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -6, scale: 1.03 }}
              transition={{ duration: 0.22 }}
              className="gold-stat-card"
              style={{
                borderRadius: "20px",
                padding: "clamp(18px, 3vw, 28px) clamp(14px, 2.5vw, 22px)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* shine sweep */}
              <div className="gold-stat-shine" />
              <div
                style={{
                  fontSize: "clamp(2rem, 4.5vw, 2.8rem)",
                  fontWeight: 900,
                  fontFamily: "'Montserrat', sans-serif",
                  letterSpacing: "-1px",
                  background: "linear-gradient(160deg, #b8730a 0%, #e6a817 40%, #ffd700 65%, #c8860a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  lineHeight: 1,
                  marginBottom: "6px",
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  color: "#888",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {s.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.4 }}
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Link
            to={ROUTES.MEET_OUR_VENDORS}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "linear-gradient(90deg, #2d6a14, #4a8c25)",
              color: "#fff",
              padding: "0.9em 2.5em",
              borderRadius: "999px",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 24px rgba(74,140,37,0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            className="wl-btn-primary"
          >
            Meet Our Vendors
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <path d="M2.5 7.5h10M7.5 2.5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
          <Link
            to={ROUTES.OUR_STORY}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "rgba(45,106,20,0.07)",
              color: "#2d6a14",
              padding: "0.9em 2.5em",
              borderRadius: "999px",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.3px",
              border: "1.5px solid rgba(109,199,68,0.4)",
              transition: "background 0.2s, border-color 0.2s",
            }}
            className="wl-btn-ghost"
          >
            Read Our Story
          </Link>
        </motion.div>
      </div>

      <style>{`
        .wl-ring {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .wl-ring-1 {
          width: clamp(300px, 40vw, 560px);
          height: clamp(300px, 40vw, 560px);
          border: 1px solid rgba(109,199,68,0.1);
          top: -160px; right: -160px;
        }
        .wl-ring-2 {
          width: clamp(200px, 28vw, 380px);
          height: clamp(200px, 28vw, 380px);
          border: 1px solid rgba(109,199,68,0.07);
          bottom: -80px; left: -80px;
        }
        .wl-blob {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(80px);
        }
        .wl-blob-1 {
          width: 400px; height: 400px;
          background: rgba(109,199,68,0.1);
          top: -100px; right: -80px;
        }
        .wl-blob-2 {
          width: 300px; height: 300px;
          background: rgba(255,165,0,0.06);
          bottom: -60px; left: -60px;
        }
        .gold-stat-card {
          background: #fff;
          border: 1.5px solid rgba(255,215,0,0.35);
          box-shadow:
            0 4px 24px rgba(45,106,20,0.08),
            0 1px 4px rgba(0,0,0,0.04),
            inset 0 1px 0 rgba(255,240,150,0.4);
        }
        .gold-stat-card:hover {
          box-shadow:
            0 0 0 1.5px rgba(255,215,0,0.45),
            0 12px 36px rgba(184,134,11,0.2),
            0 2px 8px rgba(0,0,0,0.06),
            inset 0 1px 0 rgba(255,240,150,0.5);
        }
        .gold-stat-shine {
          position: absolute;
          top: -60%;
          left: -75%;
          width: 55%;
          height: 220%;
          background: linear-gradient(105deg, transparent 40%, rgba(255,240,180,0.18) 50%, transparent 60%);
          transform: skewX(-15deg);
          animation: gold-shine 3.2s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes gold-shine {
          0%   { left: -75%; opacity: 0; }
          10%  { opacity: 1; }
          40%  { left: 130%; opacity: 1; }
          50%  { opacity: 0; }
          100% { left: 130%; opacity: 0; }
        }
        .wl-btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(74,140,37,0.45) !important;
        }
        .wl-btn-ghost:hover {
          background: rgba(109,199,68,0.1) !important;
          border-color: rgba(109,199,68,0.6) !important;
        }
      `}</style>
    </section>
  );
}
